/* eslint-disable react/jsx-props-no-spreading */
import SentimentDissatisfiedIcon from "@mui/icons-material/SentimentDissatisfied"
import SentimentSatisfiedIcon from "@mui/icons-material/SentimentSatisfied"
import SentimentSatisfiedAltIcon from "@mui/icons-material/SentimentSatisfiedAltOutlined"
import SentimentVeryDissatisfiedIcon from "@mui/icons-material/SentimentVeryDissatisfied"
import SentimentVerySatisfiedIcon from "@mui/icons-material/SentimentVerySatisfied"
import {
    Divider,
    IconButton,
    Stack,
    TextField,
    Typography,
} from "@mui/material"
import React, { ChangeEvent, ReactElement } from "react"

import {
    AppCard,
    AppCardContent,
    AppCardHeader,
} from "../../../surfaces/appCard"

export enum RATE_FIELD {
    RATING = "RATING",
    COMMENT = "COMMENT",
}

type RatingInput = {
    title: string
    question: string
    onUpdate: (field: RATE_FIELD, value: string | number | null) => void
    topDivider?: boolean
    bottomDivider?: boolean
}

type RatingIcons = {
    icon: React.ElementType
    color: string
    label: string
    score: number
}

type RatingIconProps = {
    icons: RatingIcons[]
    onUpdate: (field: RATE_FIELD, value: number) => void
    currentRating: number | null
}

const customIcons: RatingIcons[] = [
    {
        icon: SentimentVeryDissatisfiedIcon,
        color: "error",
        label: "Very Dissatisfied",
        score: 1,
    },
    {
        icon: SentimentDissatisfiedIcon,
        color: "error",
        label: "Dissatisfied",
        score: 2,
    },
    {
        icon: SentimentSatisfiedIcon,
        color: "warning",
        label: "Neutral",
        score: 3,
    },
    {
        icon: SentimentSatisfiedAltIcon,
        color: "success",
        label: "Satisfied",
        score: 4,
    },
    {
        icon: SentimentVerySatisfiedIcon,
        color: "success",
        label: "Very Satisfied",
        score: 5,
    },
]

/**
 * Displays a custom rating system with icons and labels
 * - attempts to fix some of the constraints with the default rating system from MUI
 * @param icons The icons to display
 * @param onUpdate The function to call when the rating is updated
 * @param currentRating The current rating
 * @returns Custom rating system
 */
export const CustomRating = ({
    icons,
    onUpdate,
    currentRating,
}: RatingIconProps): ReactElement => (
    <Stack flexDirection="row">
        {icons.map((rating) => {
            const isSelected = currentRating === rating.score
            return (
                <Stack key={rating.label}>
                    <IconButton
                        disableRipple
                        onClick={() => {
                            onUpdate(RATE_FIELD.RATING, rating.score)
                        }}
                    >
                        <rating.icon
                            color={rating.color}
                            sx={{
                                fontSize: "3rem",
                                transform: isSelected ? "scale(1.4)" : "none",
                                "&:hover": !isSelected && {
                                    transform: "scale(1.4) rotateY(-180deg)",
                                    transition: "0.4s ease-in-out",
                                    transitionDelay: "0.2s", // Add this line
                                },
                                "&:not(:hover)": {
                                    transitionDelay: "0s",
                                },
                            }}
                        />
                    </IconButton>
                    <Typography
                        fontWeight={isSelected ? "bold" : "normal"}
                        variant={isSelected ? "h5" : "h6"}
                    >
                        {rating.score}
                    </Typography>
                </Stack>
            )
        })}
    </Stack>
)

/**
 * Displays a feedback card with a rating system between 1 and 5, denoting the user's satisfaction
 * @param title The title of the feedback card
 * @param question The question to display on the feedback card
 * @param onUpdate The function to call when the rating is updated
 * @param topDivider Whether to display a divider at the top of the card
 * @param bottomDivider Whether to display a divider at the bottom of the card
 * @returns Feedback card with rating system
 */
const Feedback = ({
    title,
    question,
    onUpdate,
    topDivider = true,
    bottomDivider = true,
}: RatingInput): ReactElement => {
    const [showTextArea, setShowTextArea] = React.useState(false)
    const [rating, setRating] = React.useState<number | null>(-1)

    const updateRating = (_: any, value: number | null): void => {
        setRating(value)
        onUpdate(RATE_FIELD.RATING, value)
        if (!showTextArea) setShowTextArea(true)
    }

    return (
        <AppCard sx={{ py: 0 }}>
            {topDivider && <Divider sx={{ mb: 2 }} />}
            <AppCardHeader
                title={title}
                subheader={question}
                titleTypographyProps={{
                    color: "primary.main",
                    variant: "h6",
                    align: "center",
                    fontWeight: "500",
                }}
                subheaderTypographyProps={{
                    align: "center",
                }}
            />
            <AppCardContent>
                <Stack direction="column" spacing={3} alignItems="center">
                    <CustomRating
                        currentRating={rating}
                        icons={customIcons}
                        onUpdate={updateRating}
                    />
                    {showTextArea && (
                        <TextField
                            label="Tell us why!"
                            placeholder="Feel free to leave a comment..."
                            multiline
                            onChange={(e: ChangeEvent<HTMLInputElement>) =>
                                onUpdate(RATE_FIELD.COMMENT, e.target.value)
                            }
                        />
                    )}
                </Stack>
            </AppCardContent>
            {bottomDivider && <Divider sx={{ mt: 3 }} />}
        </AppCard>
    )
}

Feedback.defaultProps = {
    topDivider: true,
    bottomDivider: true,
}

export default Feedback
